import { set, findIndex, omit, concat, map } from 'lodash'
import { http, queryString } from 'utils'
import { LoadDecorator } from 'decorators'

// Weighbridge mutation imports.
import {
  SET_LOADS, SET_LOADS_PAGINATION, ADD_LOADS, SET_WEIGHBRIDGE, SET_WEIGHBRIDGE_ATTRIBUTE,
  RESET_WEIGHBRIDGE, SET_DOCKET_ATTRIBUTE, ADD_DOCKET, REMOVE_DOCKET
} from 'vx/mutations'

const initialWeighbridgeState = () => {
  return {
    id: null,
    load_id: null,
    job_id: null,
    total_tonnage: null,
    date: null,
    dockets: []
  }
}

// Weighbridge state.
const state = {
  loads: [],
  pagination: {},
  weighbridge: initialWeighbridgeState()
}

// Weighbridge state getters.
const getters = {
  /**
   * Loads decorated via the LoadDecorator.
   */
  loadsDecorated: state => map(state.loads, load => (new LoadDecorator(load)))
}

// Weighbridge actions.
const actions = {
  /**
   * Get weighbridge loads.
   */
  getWeighbridgeLoads ({ commit }, payload) {
    return http.get(`loads?${queryString(payload)}&weighbridge`).then(response => {
      commit(SET_LOADS, response.data.data)
      commit(SET_LOADS_PAGINATION, omit(response.data, 'data'))

      return response
    })
  },

  /**
   * Get weighbridge loads for the next page.
   */
  getWeighbridgeLoadsForNextPage ({ commit, state }, payload) {
    if (state.pagination.current_page === state.pagination.last_page || state.pagination.total === 0) {
      return Promise.reject('There are no more loads to load.')
    }

    let page = state.pagination.current_page + 1

    return http.get(`loads?${queryString(payload)}&page=${page}&weighbridge`).then(response => {
      commit(ADD_LOADS, response.data.data)
      commit(SET_LOADS_PAGINATION, omit(response.data, 'data'))

      return response
    })
  },

  /**
   * Store a new weighbridge.
   */
  storeWeighbridge ({ commit, rootState }, { load, payload }) {
    return http.post(`loads/${load}/weighbridges`, payload)
  },

  /**
   * Update a weighbridge.
   */
  updateWeighbridge ({ commit }, { id, payload }) {
    return http.put(`weighbridges/${id}`, payload)
  }
}

// Weighbridge state mutations.
const mutations = {
  /**
   * Set the weighbridge loads.
   */
  [SET_LOADS] (state, loads) {
    state.loads = loads
  },

  /**
   * Set the weighbridge loads pagination.
   */
  [SET_LOADS_PAGINATION] (state, pagination) {
    state.pagination = pagination
  },

  /**
   * Add more loads to the weighbridge loads state.
   */
  [ADD_LOADS] (state, loads) {
    state.weighbridgeLoads = concat(state.weighbridgeLoads, loads)
  },

  /**
   * Sets the weighbridge state from a load.
   */
  [SET_WEIGHBRIDGE] (state, weighbridge) {
    state.weighbridge = weighbridge
  },

  /**
   * Set a weighbridge attribute.
   */
  [SET_WEIGHBRIDGE_ATTRIBUTE] (state, { attribute, value }) {
    set(state.weighbridge, attribute, value)
  },

  /**
   * Reset the weighbridge.
   */
  [RESET_WEIGHBRIDGE] (state) {
    state.weighbridge = initialWeighbridgeState()
  },

  /**
   * Add a docket to the weighbridges dockets.
   */
  [ADD_DOCKET] (state, docket) {
    let existingDocket = docket.index === undefined
      ? findIndex(state.weighbridge.dockets, existing => existing.id === docket.id)
      : docket.index

    if (existingDocket === -1) {
      state.weighbridge.dockets.push(docket)
    } else {
      state.weighbridge.dockets.splice(existingDocket, 1, docket)
    }
  },

  /**
   * Remove a docket from the weighbridge either by ID or by its index.
   */
  [REMOVE_DOCKET] (state, { id, index }) {
    if (index === undefined) {
      index = findIndex(state.weighbridge.dockets, docket => docket.id === id)
    }

    state.weighbridge.dockets.splice(index, 1)
  },

  /**
   * Set a docket attribute on a weighbridge docket identified by ID or index.
   */
  [SET_DOCKET_ATTRIBUTE] (state, { id, index, attribute, value }) {
    if (index === undefined) {
      index = findIndex(state.weighbridge.dockets, docket => docket.id === id)
    }

    set(state.weighbridge.dockets[index], attribute, value)
  }
}

// Weighbridge modules.
import docket from './weighbridge/docket'

const modules = {
  docket
}

// Export our modules, state, getters, actions, and mutations.
export default {
  namespaced: true, modules, state, getters, actions, mutations
}



// WEBPACK FOOTER //
// ./src/vuex/modules/weighbridge.js